import { MessageSquare, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useNotify,
  useRefresh,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  activeMentionQuery,
  applyMention,
  fullName,
  matchPeople,
  parseBody,
  type MentionTarget,
} from "./mentions";
import { useTeammates } from "./useTeammates";

/** Which record this thread belongs to, exactly one, mirroring the DB check. */
export type CommentSubject =
  | { contact_id: number }
  | { company_id: number }
  | { deal_id: number }
  | { job_id: string }
  | { task_id: number }
  | { estimate_id: string }
  | { invoice_id: string };

interface Comment {
  id: string;
  body: string;
  author_id: number | null;
  created_at: string;
}

const subjectFilter = (subject: CommentSubject) =>
  subject as Record<string, unknown>;

const timeAgo = (iso: string) => {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * Internal comments on a record, the team's discussion, never shown to the
 * customer (unlike notes, which feed the shared timeline).
 *
 * Mentions are stored as `@[Name](sales:id)` and resolved server-side; the
 * picker here only helps compose that token. Deleting is the author's alone,
 * enforced by RLS, so the button is hidden for everyone else rather than
 * offering an action the server will refuse.
 */
export const CommentThread = ({
  subject,
  className,
  hideHeader,
}: {
  subject: CommentSubject;
  className?: string;
  /** The parent card already names this section, do not repeat the title. */
  hideHeader?: boolean;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [deleteOne] = useDelete();
  const { identity } = useGetIdentity();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<{ query: string; start: number } | null>(
    null,
  );
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { data: comments, isPending } = useGetList<Comment>("comments", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
    filter: subjectFilter(subject),
  });

  // Workspace members only, see useTeammates for why this is not a plain
  // list of `sales`.
  const { data: team } = useTeammates();

  const byId = useMemo(() => {
    const map = new Map<number, MentionTarget>();
    (team ?? []).forEach((person) => map.set(person.id, person));
    return map;
  }, [team]);

  const suggestions = picker ? matchPeople(team ?? [], picker.query) : [];

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBody(event.target.value);
    setPicker(
      activeMentionQuery(event.target.value, event.target.selectionStart ?? 0),
    );
  };

  const choose = (person: MentionTarget) => {
    if (!picker) return;
    const caret = inputRef.current?.selectionStart ?? body.length;
    const next = applyMention(body, picker.start, caret, person);
    setBody(next.text);
    setPicker(null);
    // Restore the caret after React re-renders, or the user types mid-token.
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  const submit = () => {
    if (!body.trim()) return;
    setBusy(true);
    create(
      "comments",
      { data: { ...subject, body: body.trim() } },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setBody("");
          setBusy(false);
          refresh();
        },
        onError: (error: unknown) => {
          setBusy(false);
          notify(
            error instanceof Error
              ? error.message
              : "Could not post the comment",
            { type: "error" },
          );
        },
      },
    );
  };

  return (
    <div className={cn("flex min-w-0 flex-col gap-3", className)}>
      {hideHeader ? (
        <p className="text-[12px] text-muted-foreground">
          Only your team can see these
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <MessageSquare
            className="size-4 text-muted-foreground"
            strokeWidth={1.75}
          />
          <h3 className="text-[14px] font-semibold tracking-tight text-foreground">
            Internal comments
          </h3>
          <span className="text-[12px] text-muted-foreground">
            Only your team can see these
          </span>
        </div>
      )}

      {isPending ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : (comments ?? []).length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          No comments yet. Type @ to bring a colleague in.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {(comments ?? []).map((comment) => {
            const author = comment.author_id
              ? byId.get(comment.author_id)
              : null;
            const isMine = comment.author_id === Number(identity?.id);
            return (
              <li key={comment.id} className="min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-[13px] font-medium text-foreground">
                    {author ? fullName(author) : "Removed user"}
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-[11.5px] text-muted-foreground">
                    {timeAgo(comment.created_at)}
                    {isMine ? (
                      <button
                        aria-label="Delete comment"
                        className="text-muted-foreground transition-colors hover:text-destructive"
                        onClick={() =>
                          deleteOne(
                            "comments",
                            { id: comment.id, previousData: comment },
                            {
                              mutationMode: "pessimistic",
                              onSuccess: () => refresh(),
                            },
                          )
                        }
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    ) : null}
                  </span>
                </div>
                <p className="whitespace-pre-wrap break-words text-[13px] leading-5 text-foreground">
                  {parseBody(comment.body).map((segment, index) =>
                    segment.kind === "mention" ? (
                      <span
                        key={index}
                        className="rounded bg-primary/10 px-1 font-medium text-primary"
                      >
                        @
                        {byId.get(segment.salesId)
                          ? fullName(byId.get(segment.salesId)!)
                          : segment.label}
                      </span>
                    ) : (
                      <span key={index}>{segment.text}</span>
                    ),
                  )}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      <div className="relative">
        <textarea
          ref={inputRef}
          rows={2}
          value={body}
          onChange={onChange}
          placeholder="Add an internal comment… type @ to mention someone"
          className="w-full resize-y rounded-md border border-input bg-transparent px-2.5 py-2 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onKeyDown={(event) => {
            if (event.key === "Escape") setPicker(null);
            // Enter sends; Shift+Enter is a newline. Comments are short.
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !suggestions.length
            ) {
              event.preventDefault();
              submit();
            }
            if (event.key === "Enter" && suggestions.length) {
              event.preventDefault();
              choose(suggestions[0]);
            }
          }}
        />

        {suggestions.length ? (
          <ul className="absolute bottom-full z-20 mb-1 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-md">
            {suggestions.map((person) => (
              <li key={person.id}>
                <button
                  className="w-full px-3 py-1.5 text-left text-[13px] text-popover-foreground hover:bg-accent"
                  onMouseDown={(event) => {
                    // mousedown, not click: the textarea must not lose focus
                    // before the token is inserted.
                    event.preventDefault();
                    choose(person);
                  }}
                >
                  {fullName(person)}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-2 flex justify-end">
          <Button size="sm" disabled={busy || !body.trim()} onClick={submit}>
            Comment
          </Button>
        </div>
      </div>
    </div>
  );
};
